import {
  Accordion,
  AccordionItem,
  Body1,
  Box,
  Button,
  ButtonGroup,
  Container,
  Display2,
  Flex,
  Label1,
  Link,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@sainsburys-tech/fable'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getRangedSkusByStore } from '../../../common/api/skuApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import BackButton from '../../../common/components/BackButton/BackButton'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { getUserStore } from '../../../utils/localStore'
import { getMixes } from '../../api/mixApi'
import { getPlan } from '../../api/planApi'
import { createAdditionalWave } from '../../api/waveApi'
import { IMix } from '../../types/IMix'
import { IPlan } from '../../types/IPlan'
import { IPlannerGroup } from '../../types/IPlannerGroup'
import { ISelectedGroup } from '../../types/ISelectedGroup'
import AdditionalWaveTable from './AdditionalWaveTable'

const AdditionalWaveView = () => {
  const { dispatch, state } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()
  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState<ISelectedGroup[]>([])
  const [plan, setPlan] = useState<IPlan | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [plannerGroups, setPlannerGroups] = useState<IPlannerGroup[]>([])
  const [nextWaveNumber, setNextWaveNumber] = useState<number>(1)
  const [mixes, setMixes] = useState<IMix[]>([])
  const [successMessage, setSuccessMessage] = useState('')
  const [selectedPlannerGroupId, setSelectedPlannerGroupId] = useState<
    number | null
  >(null)
  const [rangedSkus, setRangedSkus] = useState<number[]>([])
  const [tempSkus, setTempSkus] = useState<
    {
      skuId: number
      skuName: string
      quantity: number
    }[]
  >([])

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      useErrorHandler(setErrorMessage).handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    setIsDataLoading(true)
    const today = dayjs().format('YYYY-MM-DD')
    Promise.all([
      getPlan(apiClient, today),
      getRangedSkusByStore(apiClient, store.storeId, today, true),
    ])
      .then(async ([plan, skus]) => {
        setPlan(plan)
        const allPlannerGroups = plan.planControlGroups
          ? plan.planControlGroups.flatMap((cg) => cg.plannerGroups)
          : []
        setPlannerGroups(allPlannerGroups)
        setRangedSkus(skus.map((sku) => sku.skuId))

        const mixes = await getMixes(
          apiClient,
          store.storeId,
          new Date(plan.planDate),
        )
        setMixes(mixes)
        const maxWave =
          mixes.length > 0 ? Math.max(...mixes.map((mix) => mix.wave ?? 0)) : 0
        setNextWaveNumber(maxWave + 1)
      })
      .catch(() => {
        setErrorMessage(ErrorMessage.GeneralException)
      })
      .finally(() => setIsDataLoading(false))
  }, [apiClient, store.storeId])

  const onPlannerGroupChange = (value: string) => {
    setErrorMessage('')
    setSuccessMessage('')
    const plannerGroupId = Number(value)
    setSelectedPlannerGroupId(plannerGroupId)
    const plannerGroup = plannerGroups.find(
      (pg) => pg.plannerGroupId === plannerGroupId,
    )
    const existingGroup = selectedGroups.find(
      (g) => g.plannerGroupId === plannerGroupId,
    )
    const filteredSkus = plannerGroup
      ? plannerGroup.planItems.filter((sku) => rangedSkus.includes(sku.skuId))
      : []
    setTempSkus(
      existingGroup
        ? existingGroup.skus
        : filteredSkus.map((sku) => ({
            skuId: sku.skuId,
            skuName: sku.skuName,
            quantity: 0,
          })),
    )
  }

  const groupsWithItems = useMemo(
    () =>
      selectedGroups.filter((group) =>
        group.skus.some((sku) => sku.quantity > 0),
      ),
    [selectedGroups],
  )

  const hasGroupsWithItems = () => groupsWithItems.length > 0

  const handleTempQuantityChange = (skuId: number, value: number) => {
    setTempSkus((prev) =>
      prev.map((sku) =>
        sku.skuId === skuId ? { ...sku, quantity: value } : sku,
      ),
    )
  }

  const handleAddPlannerGroup = (plannerGroupId: number) => {
    if (tempSkus.length === 0 || !tempSkus.some((sku) => sku.quantity > 0)) {
      setErrorMessage(
        'Please add at least one SKU with a quantity greater than 0.',
      )
      return
    }
    const plannerGroup = plannerGroups.find(
      (pg) => pg.plannerGroupId === plannerGroupId,
    )
    if (!plannerGroup) return

    setSelectedGroups((prev) => {
      const exists = prev.some((g) => g.plannerGroupId === plannerGroupId)
      if (exists) {
        return prev.map((g) =>
          g.plannerGroupId === plannerGroupId ? { ...g, skus: tempSkus } : g,
        )
      } else {
        return [
          ...prev,
          {
            plannerGroupId,
            plannerGroupName: plannerGroup.plannerGroupName,
            skus: tempSkus,
          },
        ]
      }
    })
    setSelectedPlannerGroupId(null)
    setTempSkus([])
    setErrorMessage('')
  }

  const getLatestMixForGroup = (plannerGroupId: number) => {
    const groupMixes = mixes.filter(
      (mix) => mix.plannerGroupId === plannerGroupId,
    )
    if (groupMixes.length === 0) return undefined
    return groupMixes.reduce((a, b) => ((a.wave ?? 0) > (b.wave ?? 0) ? a : b))
  }

  const handleSave = async () => {
    if (!plan) return
    const plannerGroupsPayload = selectedGroups.map((group) => ({
      PlannerGroupId: group.plannerGroupId,
      OvenSettingId: null,
      OvenTypeId: null,
      Skus: group.skus
        .filter((sku) => sku.quantity > 0)
        .map((sku) => ({
          SkuId: sku.skuId,
          ProductionTarget: sku.quantity,
          Marked: false,
        })),
    }))
    const filteredPlannerGroups = plannerGroupsPayload.filter(
      (group) => group.Skus.length > 0,
    )
    const payload = {
      PlanId: plan.planId,
      StoreCode: store.storeId,
      WaveNumber: nextWaveNumber,
      PlannerGroups: filteredPlannerGroups,
      CreatedBy: state.user?.email || null,
    }
    if (filteredPlannerGroups.length === 0) {
      setErrorMessage(
        'Please add at least one SKU with a quantity greater than 0 to create the additional wave.',
      )
      return
    }
    try {
      const response = await createAdditionalWave(apiClient, payload)

      if (!response.isSuccess) {
        window.scrollTo(0, 0)
        setErrorMessage(
          response.message ||
            'Failed to create additional wave. Please try again.',
        )
        setSuccessMessage('')
        return
      }
      window.scrollTo(0, 0)
      setSuccessMessage(
        'Additional wave created successfully! Returning to Production Page...',
      )
      setErrorMessage('')
      setTimeout(() => {
        navigate('/bakeryview')
      }, 2000)
    } catch (error: any) {
      window.scrollTo(0, 0)
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create additional wave. Please try again.'
      setErrorMessage(errorMsg)
      setSuccessMessage('')
    }
  }

  return (
    <>
      {isLoading && (
        <ProgressLoading message='Loading...' testid='loading-indicator' />
      )}

      {!isLoading && errorMessage && <ErrorRefresh message={errorMessage} />}

      {!isLoading && !errorMessage && (
        <Box padding='none' testid='additional-wave-view'>
          <Header
            title={headerPlannerTitles.bakery}
            navItems={headerNavigationItems}
            activeMenuType={Menu.ProductionView}
          />
          <Container className='c-common-main-view-content' size='xl'>
            {BackButton('/bakeryview', 'Back to Production')}
            {successMessage && (
              <Box
                style={{
                  marginBottom: '1rem',
                  background:
                    'var(--fable-color-background-success-bold-light, #225b24)',
                  color:
                    'var(--fable-color-background-secondary-default, #ffffff)',
                  padding: '1rem',
                  borderRadius: '4px',
                }}
              >
                <Body1>{successMessage}</Body1>
                <Link
                  href='/bakeryview'
                  style={{
                    color:
                      'var(--fable-color-background-secondary-default, #ffffff)',
                    textDecoration: 'underline',
                    marginLeft: '1rem',
                    fontWeight: 600,
                  }}
                >
                  Return now
                </Link>
              </Box>
            )}
            <Display2 style={{ marginBottom: '1.5rem', marginTop: '1.0rem' }}>
              Create an additional wave
            </Display2>
            <Body1
              style={{
                marginBottom: '1.5rem',
              }}
            >
              You are creating a new wave. Select a planner group to see
              available SKUs and add them to your new wave. You can add multiple
              planner groups to this wave as required.
            </Body1>
            <Box
              className='bakery-create-additional-wave-box'
              backgroundColor='secondary'
            >
              <Flex
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                style={{ marginBottom: '1.5rem' }}
              >
                <Select
                  label='Please select a Planner Group'
                  name='plannerGroupId'
                  placeholder='Select a planner group'
                  options={plannerGroups
                    .filter((pg) =>
                      pg.planItems.some((sku) =>
                        rangedSkus.includes(sku.skuId),
                      ),
                    )
                    .map((pg) => ({
                      key: pg.plannerGroupId,
                      label: pg.plannerGroupName,
                      value: pg.plannerGroupId.toString(),
                    }))}
                  value={
                    selectedPlannerGroupId
                      ? selectedPlannerGroupId.toString()
                      : ''
                  }
                  onChange={onPlannerGroupChange}
                  testid='planner-group-select'
                  style={{ minWidth: 220 }}
                  status={{ type: 'error', message: errorMessage }}
                />
              </Flex>
              {selectedPlannerGroupId && tempSkus.length > 0 && (
                <Box>
                  <AdditionalWaveTable
                    skus={tempSkus}
                    onChange={handleTempQuantityChange}
                    isSkuInAdditionalWave={(skuId) =>
                      selectedGroups.some((group) =>
                        group.skus.some(
                          (sku) => sku.skuId === skuId && sku.quantity > 0,
                        ),
                      )
                    }
                  />
                  <Button
                    style={{ marginTop: '1rem', backgroundColor: 'white' }}
                    disabled={!tempSkus.some((sku) => sku.quantity > 0)}
                    onClick={() => {
                      if (selectedPlannerGroupId)
                        handleAddPlannerGroup(selectedPlannerGroupId)
                      window.scrollTo(0, 0)
                    }}
                    variant='secondary'
                  >
                    Save to Additional Wave
                  </Button>
                </Box>
              )}
              <Accordion size='md' style={{ marginTop: '2rem' }}>
                <AccordionItem label='Items in Additional Wave'>
                  {!hasGroupsWithItems() && (
                    <Body1>No planner groups selected.</Body1>
                  )}
                  {groupsWithItems.map((group) => {
                    const latestMix = getLatestMixForGroup(group.plannerGroupId)
                    return (
                      <Box
                        key={group.plannerGroupId}
                        style={{ margin: '1.5rem 0' }}
                      >
                        <Label1
                          style={{
                            marginBottom: '0.5rem',
                            display: 'block',
                          }}
                        >
                          {latestMix?.ovenSettingDetail
                            ? `${group.plannerGroupName} (${latestMix.ovenSettingDetail.ovenTypeDescription} - ${latestMix.ovenSettingDetail.ovenSettingDescription})`
                            : group.plannerGroupName}
                        </Label1>
                        <Table
                          testid='additional-wave-sku-table'
                          className='c-bakery-additional-wave-added-table'
                          layout='auto'
                        >
                          <TableHeader>
                            <TableRow>
                              <TableCell className='c-additional-wave-added-product'>
                                <Label1>Product</Label1>
                              </TableCell>
                              <TableCell className='c-additional-wave-added-sku-code'>
                                <Label1>SKU Code</Label1>
                              </TableCell>
                              <TableCell className='c-additional-wave-added-quantity'>
                                <Label1>Quantity</Label1>
                              </TableCell>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.skus
                              .filter((sku) => sku.quantity > 0)
                              .map((sku) => (
                                <TableRow key={sku.skuId}>
                                  <TableCell>{sku.skuName}</TableCell>
                                  <TableCell>{sku.skuId}</TableCell>
                                  <TableCell>{sku.quantity}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )
                  })}
                </AccordionItem>
              </Accordion>
              <ButtonGroup
                style={{ marginTop: '2rem' }}
                orientation='horizontal'
              >
                <Button disabled={!hasGroupsWithItems()} onClick={handleSave}>
                  Create Additional Wave
                </Button>
                <Button
                  variant='tertiary'
                  onClick={() => navigate('/bakeryview')}
                >
                  Cancel
                </Button>
              </ButtonGroup>
            </Box>
          </Container>
        </Box>
      )}
    </>
  )
}

export default AdditionalWaveView
